import { z } from 'zod';

import * as util from './urls';
import { RequiredStringVariableSchema } from './util';

/**
 * The environment schema for the Auth0 SDK.
 *
 * Note: By default, the Auth0 SDK looks for environment variables named `AUTH0_DOMAIN` and
 * `APP_BASE_URL` to configure the `domain` and `appBaseUrl` properties of the `Auth0Client`.
 * However, during the migration of v3 -> v4, it was easier to retain the names of the existing
 * environment variables (`AUTH0_ISSUER_BASE_URL` and `AUTH0_BASE_URL`, respectively) rather than
 * renaming them to match the SDK's expectations. This works because the SDK's environment variable
 * discovery only takes effect if they are not explicitly provided to the `Auth0Client` constructor,
 * which is what this application does.
 *
 * In the future, it may make sense to rename these variables to match the SDK's expectations and
 * eliminate the need for the direct/manual inclusion of these values when instantiating the
 * {@link Auth0Client} - but that would require an extensive renaming of environment variables
 * throughout the application's environment configuration, including AWS SSM parameters, GitHub
 * repository variables, etc.
 *
 * The mapping between the retained variable names and the names that the v4 SDK discovers on
 * `process.env` by default is as follows:
 *
 * | Retained Variable Name  | v4 SDK Variable Name | Example Value              |
 * | ----------------------- | -------------------- | -------------------------- |
 * | `AUTH0_BASE_URL`        | `APP_BASE_URL`       | `https://app.example.com`  |
 * | `AUTH0_ISSUER_BASE_URL` | `AUTH0_DOMAIN`       | `https://auth.example.com` |
 *
 * Note: The v4 SDK expects `AUTH0_DOMAIN` to hold the bare hostname (e.g. 'auth.example.com'),
 * whereas the retained `AUTH0_ISSUER_BASE_URL` holds the URL form - the `domain` field's schema
 * derives the bare hostname from that URL form via a transform.
 */
export const Auth0EnvironmentSchema = z.object({
  appBaseUrl: util.UrlSchema,
  clientId: RequiredStringVariableSchema,
  clientSecret: RequiredStringVariableSchema,
  /**
   * The bare hostname of the Auth0 tenant (e.g. 'auth.example.com'), derived from the URL form
   * (e.g. 'https://auth.example.com') that the `AUTH0_ISSUER_BASE_URL` environment variable is
   * contractually expected to hold.
   */
  domain: util.UrlSchema.transform(value => new URL(value).hostname),
  m2mBaseUrl: z.string().url(),
  m2mClientId: RequiredStringVariableSchema,
  m2mClientSecret: RequiredStringVariableSchema,
  secret: RequiredStringVariableSchema,
});

export type Auth0EnvironmentVars = z.output<typeof Auth0EnvironmentSchema>;

type Auth0Input = {
  [key in keyof z.input<typeof Auth0EnvironmentSchema>]?:
    | undefined
    | z.input<typeof Auth0EnvironmentSchema>[key];
};

export const parseAuth0ProcessEnv = (): Auth0Input => ({
  /**
   * @see {@link Auth0EnvironmentSchema} for an explanation of why the `AUTH0_BASE_URL` and
   * `AUTH0_ISSUER_BASE_URL` variables are retained here and how they map to the v4 SDK's
   * expected variable names.
   */
  appBaseUrl: process.env.AUTH0_BASE_URL,
  clientId: process.env.AUTH0_CLIENT_ID,
  clientSecret: process.env.AUTH0_CLIENT_SECRET,
  /**
   * @see {@link Auth0EnvironmentSchema} for an explanation of why the `AUTH0_BASE_URL` and
   * `AUTH0_ISSUER_BASE_URL` variables are retained here and how they map to the v4 SDK's
   * expected variable names.
   */
  domain: process.env.AUTH0_ISSUER_BASE_URL,
  m2mBaseUrl: process.env.AUTH0_M2M_API_BASE_URL,
  m2mClientId: process.env.AUTH0_M2M_CLIENT_ID,
  m2mClientSecret: process.env.AUTH0_M2M_CLIENT_SECRET,
  secret: process.env.AUTH0_SECRET,
});
